import { ElementProps } from '@/components/sections/builder/types';
import { useBuilderStore } from '@/stores/builder';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';
import { TextWithIconElementDataType } from '@/components/sections/builder/elements/body/text-with-icon/type';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import BackgroundSetting from '@/components/sections/builder/settings/background-setting';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';

const IMAGE_WIDTHS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

const IMAGE_POSITION = [
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
  { label: 'Top Center', value: 'top' },
  { label: 'Top Left', value: 'top-left' },
  { label: 'Top Right', value: 'top-right' },
  { label: 'Bottom Center', value: 'bottom' },
  { label: 'Bottom Left', value: 'bottom-left' },
  { label: 'Bottom Right', value: 'bottom-right' },
];

const BORDER_STYLE = [
  { value: 'none', label: 'None' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'solid', label: 'Solid' },
];

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const content = element.data as TextWithIconElementDataType;

  return (
    <div>
      <div className='mb-4 mt-4 font-bold'>Heading</div>
      <TextEditorMinimal
        placeholder='Label'
        onChange={(value) =>
          builder.setElementData(element.id, {
            heading: value,
          })
        }
        value={content.heading}
        className='mb-4 w-full '
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-4 mt-4 font-bold'>Heading Size</div>
      <InputTab
        defaultValue={content.headingSize}
        options={[
          { label: 'Small', value: 'small' },
          { label: 'Medium', value: 'medium' },
          { label: 'Large', value: 'large' },
        ]}
        onChange={(v) =>
          builder.setElementData(element.id, {
            headingSize: v,
          })
        }
      />
      <div className='mb-4 mt-4 font-bold'>Title alignment</div>
      <InputTab
        defaultValue={content.titleAlign}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) =>
          builder.setElementData(element.id, {
            titleAlign: v,
          })
        }
      />
      <div className='mb-4 mt-4 font-bold'>Image Size</div>
      <div>
        <Select
          name='imageWidth'
          value={content.imageWidth}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              imageWidth: val,
            })
          }
        >
          <SelectTrigger className=''>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {IMAGE_WIDTHS.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className='mb-4 mt-4 font-bold'>Image Position</div>
      <div>
        <Select
          name='imagePosition'
          value={content.imagePosition}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              imagePosition: val,
            })
          }
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {IMAGE_POSITION.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <InputSlider
        label='Number of columns on desktop'
        value={content.numberOfColumns}
        min={1}
        max={6}
        onChange={(val) =>
          builder.setElementData(element.id, {
            numberOfColumns: val,
          })
        }
      />
      <div className='mb-4 mt-4 font-bold'>Column Alignment</div>
      <InputTab
        grid={2}
        defaultValue={content.columnAlignment}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { columnAlignment: v })}
      />
      <div className='mb-4 mt-4 font-bold'>Color Scheme</div>
      <SchemeSetting
        value={content.colorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            colorScheme: theme,
          });
        }}
      />
      <div className='mb-1 text-sm md:mt-4'>Border Style</div>
      <div>
        <Select
          name='borderStyle'
          value={content.borderStyle}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              borderStyle: val,
            })
          }
        >
          <SelectTrigger className=''>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {BORDER_STYLE.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <InputSlider
        label='Border radius'
        value={content.borderRadius}
        min={1}
        max={32}
        onChange={(val) =>
          builder.setElementData(element.id, {
            borderRadius: val,
          })
        }
      />
      <PaddingSetting
        value={content.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />
      <BackgroundSetting
        value={content.background}
        onChange={(background) =>
          builder.setElementData(element.id, {
            background,
          })
        }
      />
    </div>
  );
}
